// Lightweight, DOM-only view for the Tournament Registration/Bracket/Results screen
import { BracketView, BracketData, NameResolver, MatchLiveStatus } from './BracketView';
import { avatarDataUriForName } from '../avatar';
import { StandingsView } from './StandingsView';
import type { StandingsViewEntry } from './StandingsView';
import { RoundRobinScheduleView } from './RoundRobinScheduleView';
import type { RoundRobinScheduleOptions } from './RoundRobinScheduleView';
import type { RoundRobinMatch } from './RoundRobinScheduler';

export type TournamentPlayerVM = {
  userId: string;
  userName: string;
  profileImageUrl?: string | null;
  seed?: number | null;
  isHost?: boolean;
};

export type TournamentLobbyViewHandlers = {
  onStart?: () => void;
  onLeave?: () => void;
  onBack?: () => void;
  onCancel?: () => void;
  onMatchClick?: (info: { round: number; matchIndex: number; aUserId?: string | null; bUserId?: string | null; result?: { aScore?: number; bScore?: number } | null }) => void;
  onPlayerProfile?: (userId: string, userName: string) => void;
  onShowBracket?: () => void;
  onShowLeaderboard?: () => void;
  onRefresh?: () => void;
};

type StandingsAwardsOptions = {
  awards?: Array<{ label: string; winner?: string; value?: string }>;
  focusAwardWinnerName?: string;
  podium?: { first?: any; second?: any; third?: any };
  isCompleted?: boolean;
  pprRows?: Array<{ userName: string; ppr: number; isMe?: boolean }>;
};

export class TournamentLobbyView {
  private root!: HTMLDivElement;
  private listEl!: HTMLDivElement;
  private countEl!: HTMLDivElement;
  private codeEl!: HTMLDivElement;
  private bracketHost!: HTMLDivElement;
  private leaderboardHost!: HTMLDivElement;
  private scheduleHost!: HTMLDivElement;
  private bracketView: BracketView | null = null;
  private standingsView: StandingsView | null = null;
  private standingsAwardsEl: HTMLDivElement | null = null;
  private scheduleView: RoundRobinScheduleView | null = null;
  private startBtn!: HTMLButtonElement | null;
  private leaveBtn!: HTMLButtonElement | null;
  private backBtn!: HTMLButtonElement | null;
  private cancelBtn!: HTMLButtonElement | null;
  private refreshBtn!: HTMLButtonElement | null;
  private leftToggle!: HTMLDivElement | null;
  private rightToggle!: HTMLDivElement | null;
  private roundBar!: HTMLDivElement | null;
  private roundLabel!: HTMLDivElement | null;
  private leftToggleLabel!: HTMLSpanElement | null;
  private rightToggleLabel!: HTMLSpanElement | null;

  constructor(private handlers: TournamentLobbyViewHandlers) {}

  public setToggleLabels(leftText: string, rightText: string) {
    if (this.leftToggleLabel) this.leftToggleLabel.textContent = leftText;
    if (this.rightToggleLabel) this.rightToggleLabel.textContent = rightText;
  }

  public show(params: { tournamentName: string; isCreator: boolean; players: TournamentPlayerVM[]; code?: string; hidePlayerList?: boolean; status?: 'registration' | 'in_progress' | 'completed'; tournamentType?: 'bracket' | 'round_robin' }) {
    this.destroy();
    this.root = document.createElement('div');
    this.root.id = 'tourney-lobby';
    this.root.innerHTML = this.template(params.tournamentName, params.isCreator, params.code, params.tournamentType);
    document.body.appendChild(this.root);

    this.listEl = this.root.querySelector('#tourneyLobbyList') as HTMLDivElement;
    this.countEl = this.root.querySelector('#tourneyPlayerCount') as HTMLDivElement;
    this.codeEl = this.root.querySelector('#tourneyCode') as HTMLDivElement;
    this.bracketHost = this.root.querySelector('#tourneyBracket') as HTMLDivElement;
    this.scheduleHost = this.root.querySelector('#tourneySchedule') as HTMLDivElement;
    this.leaderboardHost = this.root.querySelector('#tourneyLeaderboard') as HTMLDivElement;
    this.startBtn = this.root.querySelector('#tourneyStartBtn') as HTMLButtonElement | null;
    this.leaveBtn = this.root.querySelector('#tourneyLeaveBtn') as HTMLButtonElement | null;
    this.backBtn = this.root.querySelector('#tourneyBackBtn') as HTMLButtonElement | null;
    this.cancelBtn = this.root.querySelector('#tourneyCancelBtn') as HTMLButtonElement | null;
    this.refreshBtn = this.root.querySelector('#tourneyRefreshBtn') as HTMLButtonElement | null;

    if (this.startBtn) this.startBtn.onclick = () => this.handlers.onStart?.();
    if (this.leaveBtn) this.leaveBtn.onclick = () => this.handlers.onLeave?.();
    if (this.backBtn) this.backBtn.onclick = () => this.handlers.onBack?.();
    if (this.cancelBtn) this.cancelBtn.onclick = () => this.handlers.onCancel?.();
    if (this.refreshBtn) this.refreshBtn.onclick = () => this.handlers.onRefresh?.();

    // Hide actions based on phase: only show Start/Cancel during registration; don't allow Leave after start
    const phase = params.status || 'registration';
    if (phase !== 'registration') {
      if (this.startBtn) this.startBtn.style.display = 'none';
      if (this.cancelBtn) this.cancelBtn.style.display = 'none';
      if (this.leaveBtn) this.leaveBtn.style.display = 'none';
      if (this.refreshBtn) this.refreshBtn.style.display = 'none';
      // Hide join code - no longer needed after tournament starts
      if (this.codeEl) this.codeEl.style.display = 'none';
    }

    // Insert compact toggle bar under header
    const oldToggle = this.root.querySelector('.tourney-toggle') as HTMLDivElement | null;
    if (oldToggle) oldToggle.style.display = 'none';
    const headerEl = this.root.querySelector('.tourney-lobby-header') as HTMLDivElement | null;
    if (headerEl) {
      const bar = document.createElement('div');
      bar.id = 'tourneyRoundAndToggle';
      bar.style.display = 'flex';
      bar.style.alignItems = 'center';
      bar.style.justifyContent = 'flex-end';
      bar.style.margin = '4px 10px 6px 0';
      bar.innerHTML = `
        <div style="display:flex; align-items:center;">
          <span id="tourneyToggleLeftLabel" class="lobby-toggle-label">Bracket</span>
          <div class="lobby-toggle-container">
            <div id="tourneyToggleBracketPill" class="lobby-toggle selected"></div>
            <div id="tourneyToggleLeaderboardPill" class="lobby-toggle"></div>
          </div>
          <span id="tourneyToggleRightLabel" class="lobby-toggle-label">Leaderboard</span>
        </div>
      `;
      headerEl.insertAdjacentElement('afterend', bar);
    }

    // Bind pill toggles
    this.leftToggle = this.root.querySelector('#tourneyToggleBracketPill') as HTMLDivElement | null;
    this.rightToggle = this.root.querySelector('#tourneyToggleLeaderboardPill') as HTMLDivElement | null;
    this.roundBar = this.root.querySelector('#tourneyRoundAndToggle') as HTMLDivElement | null;

    // Hide toggle bar during registration phase
    if (phase === 'registration' && this.roundBar) {
      this.roundBar.style.display = 'none';
    }
    this.roundLabel = this.root.querySelector('#tourneyPlayerCount') as HTMLDivElement | null; // reuse label
    this.leftToggleLabel = this.root.querySelector('#tourneyToggleLeftLabel') as HTMLSpanElement | null;
    this.rightToggleLabel = this.root.querySelector('#tourneyToggleRightLabel') as HTMLSpanElement | null;

    if (this.leftToggle) this.leftToggle.onclick = () => {
      if (!this.leftToggle!.classList.contains('selected')) {
        this.leftToggle!.classList.add('selected');
        this.rightToggle?.classList.remove('selected');
        this.handlers.onShowBracket?.();
      }
    };
    if (this.rightToggle) this.rightToggle.onclick = () => {
      if (!this.rightToggle!.classList.contains('selected')) {
        this.rightToggle!.classList.add('selected');
        this.leftToggle?.classList.remove('selected');
        this.handlers.onShowLeaderboard?.();
      }
    };

    if (params.hidePlayerList && this.listEl) {
      this.listEl.style.display = 'none';
    }

    this.updatePlayers(params.players);
  }

  public updatePlayers(players: TournamentPlayerVM[]) {
    if (!this.listEl || !this.countEl) return;
    this.countEl.textContent = `${players.length} player${players.length === 1 ? '' : 's'} registered`;
    this.listEl.innerHTML = players.map(p => this.playerRow(p)).join('');
  }

  public destroy() {
    this.ensureScheduleHidden();
    this.ensureStandingsHidden();
    if (this.root && this.root.parentElement) {
      this.root.parentElement.removeChild(this.root);
    }
  }

  public showBracket(bracket: BracketData, currentRound: number, currentUserId?: string, resolveName?: NameResolver, focus?: { round: number; matchIndex?: number }, statuses?: Record<string, MatchLiveStatus | undefined>, isCompleted?: boolean, resolveAvatar?: (userId: string | null | undefined) => string | undefined, resolveTournamentPPR?: (userId: string | null | undefined) => number | undefined) {
    if (!this.bracketHost) return;
    this.ensureScheduleHidden();
    this.ensureStandingsHidden();
    if (this.listEl) this.listEl.style.display = 'none';
    this.bracketHost.style.display = 'block';
    if (this.leaderboardHost) this.leaderboardHost.style.display = 'none';
    if (this.scheduleHost) this.scheduleHost.style.display = 'none';

    // When showing the bracket, update header/actions to in-progress state
    if (this.startBtn) this.startBtn.style.display = 'none';
    if (this.cancelBtn) this.cancelBtn.style.display = 'none';
    if (this.leaveBtn) this.leaveBtn.style.display = 'none';
    if (this.refreshBtn) this.refreshBtn.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';

    // Toggle + round label
    this.leftToggle?.classList.add('selected');
    this.rightToggle?.classList.remove('selected');
    if (this.roundLabel) {
      const totalRounds = bracket.rounds.length;
      const matchesInRound = bracket.rounds.find(r => r.round === currentRound)?.matches.length || 0;
      let title = isCompleted ? 'Completed' : `Round ${currentRound}`;
      if (!isCompleted) {
        if (matchesInRound === 1 && currentRound === totalRounds) title = 'Championship';
        else if (matchesInRound === 2 && currentRound === totalRounds - 1) title = 'Semi-Finals';
      }
      this.roundLabel.textContent = title;
    }

    // Clear previous bracket content to avoid duplicate mounts
    try { this.bracketHost.innerHTML = ''; } catch {}
    const view = new BracketView();
    this.bracketView = view;
    view.mount(this.bracketHost);
    if (this.handlers.onMatchClick) view.setOnMatchClick(this.handlers.onMatchClick);
    if (this.handlers.onPlayerProfile) view.setOnPlayerProfile(this.handlers.onPlayerProfile);
    view.render(bracket, currentRound, currentUserId, resolveName, statuses, resolveAvatar, resolveTournamentPPR);
  }

  public showSchedule(matches: RoundRobinMatch[], options: RoundRobinScheduleOptions = {}) {
    if (!this.scheduleHost) return;
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    if (this.leaderboardHost) this.leaderboardHost.style.display = 'none';
    this.scheduleHost.style.display = 'block';
    if (this.startBtn) this.startBtn.style.display = 'none';
    if (this.cancelBtn) this.cancelBtn.style.display = 'none';
    if (this.leaveBtn) this.leaveBtn.style.display = 'none';
    if (this.refreshBtn) this.refreshBtn.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';
    this.ensureStandingsHidden();
    this.scheduleView?.destroy();
    this.scheduleView = new RoundRobinScheduleView();
    this.scheduleView.mount(this.scheduleHost);
    this.scheduleView.render(matches, options);
    this.leftToggle?.classList.add('selected');
    this.rightToggle?.classList.remove('selected');
    if (this.roundLabel) this.roundLabel.textContent = '';
  }

  public updateScheduleMatches(matches: RoundRobinMatch[], options: RoundRobinScheduleOptions = {}) {
    if (!this.scheduleView) {
      // If schedule view doesn't exist yet, render it fully instead of updating
      this.showSchedule(matches, options);
      return;
    }
    // Use the incremental update method to preserve carousel position
    this.scheduleView.updateMatches(matches, options);
  }

  public showRoundRobinStandings(entries: StandingsViewEntry[], totalMatches: number, currentUserId?: string, opts?: StandingsAwardsOptions) {
    if (!this.leaderboardHost) return;
    this.ensureScheduleHidden();
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    if (this.scheduleHost) this.scheduleHost.style.display = 'none';
    this.leaderboardHost.style.display = 'block';
    if (this.startBtn) this.startBtn.style.display = 'none';
    if (this.cancelBtn) this.cancelBtn.style.display = 'none';
    if (this.leaveBtn) this.leaveBtn.style.display = 'none';
    if (this.refreshBtn) this.refreshBtn.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';
    this.ensureStandingsHidden();
    this.standingsView = new StandingsView();
    this.standingsView.mount(this.leaderboardHost);
    this.standingsView.render(entries, totalMatches, currentUserId);
    this.leftToggle?.classList.remove('selected');
    this.rightToggle?.classList.add('selected');    
    this.removeStandingsAwardsPanel();
    if (opts?.isCompleted && opts.awards && opts.awards.length) {
      const panel = this.buildStandingsAwardsPanel({
        awards: opts.awards,
        focusAwardWinnerName: opts.focusAwardWinnerName,
        podium: opts.podium,
        pprRows: opts.pprRows
      });
      if (panel) {
        this.standingsAwardsEl = panel;
        this.leaderboardHost.appendChild(panel);
      }
    }
  }

  public showLeaderboard(rows: Array<{ userName: string; ppr: number; isMe?: boolean }>) {
    if (!this.leaderboardHost) return;
    this.ensureScheduleHidden();
    this.ensureStandingsHidden();
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    this.leaderboardHost.style.display = 'block';
    // Containment to prevent horizontal overflow on narrow screens
    this.leaderboardHost.style.maxWidth = '100%';
    this.leaderboardHost.style.overflowX = 'hidden';
    // Hide registration-only labels in non-registration phases
    if (this.countEl) this.countEl.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';
    this.rightToggle?.classList.add('selected');
    this.leftToggle?.classList.remove('selected');

    rows.sort((a,b)=> b.ppr - a.ppr);
    const container = document.createElement('div');
    container.className = 'tourney-standings-container';
    const headerWrap = document.createElement('div');
    headerWrap.className = 'tourney-standings-header';
    const title = document.createElement('div');
    title.className = 'tourney-standings-title';
    title.textContent = 'Leaderboard';
    const subtitle = document.createElement('div');
    subtitle.className = 'tourney-standings-subtitle';
    subtitle.textContent = 'Points Per Roll (PPR)';
    headerWrap.appendChild(title);
    headerWrap.appendChild(subtitle);

    const table = document.createElement('table');
    table.className = 'tourney-standings-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>Player</th>
          <th>PPR</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(r => `
          <tr class="tourney-standings-row ${r.isMe ? 'current-user' : ''}">
            <td class="name-col">${r.isMe ? `<strong>${r.userName}</strong>` : r.userName}</td>
            <td class="record-col">${r.ppr.toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    `;

    container.appendChild(headerWrap);
    container.appendChild(table);
    this.leaderboardHost.innerHTML = '';
    this.leaderboardHost.appendChild(container);
  }

  private ensureScheduleHidden() {
    if (this.scheduleHost) {
      this.scheduleHost.style.display = 'none';
    }
    this.scheduleView?.destroy();
    this.scheduleView = null;
  }

  private ensureStandingsHidden() {
    this.standingsView?.destroy();
    this.standingsView = null;
    this.removeStandingsAwardsPanel();
  }

  private removeStandingsAwardsPanel() {
    if (this.standingsAwardsEl) {
      this.standingsAwardsEl.remove();
      this.standingsAwardsEl = null;
    }
  }

  public showLeaderboardLoading() {
    if (!this.leaderboardHost) return;
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    this.leaderboardHost.style.display = 'block';
    this.rightToggle?.classList.add('selected');
    this.leftToggle?.classList.remove('selected');

    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.padding = '24px 0';
    container.innerHTML = `
      <div class="search-spinner-container"><div class="search-spinner"></div></div>
      <div style="opacity:.75; font-size:14px;">Loading leaderboard...</div>
    `;
    this.leaderboardHost.innerHTML = '';
    this.leaderboardHost.appendChild(container);
  }

  public showLeaderboardWithPodium(
    podium: { first?: any; second?: any; third?: any },
    rows: Array<{ userName: string; ppr: number; isMe?: boolean }>,
    awards?: Array<{ label: string; winner?: string; value?: string }>,
    focusAwardWinnerName?: string,
    isCompleted?: boolean
  ) {
    if (!this.leaderboardHost) return;
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    this.leaderboardHost.style.display = 'block';
    // Hide registration-only labels in non-registration phases
    if (this.countEl) this.countEl.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';
    this.rightToggle?.classList.add('selected');
    this.leftToggle?.classList.remove('selected');

    const container = document.createElement('div');
    container.className = 'tourney-standings-container';
    const headerWrap = document.createElement('div');
    headerWrap.className = 'tourney-standings-header';
    const title = document.createElement('div');
    title.className = 'tourney-standings-title';
    title.textContent = 'Leaderboard';
    const subtitle = document.createElement('div');
    subtitle.className = 'tourney-standings-subtitle';
    subtitle.textContent = 'Points Per Roll (PPR)';
    headerWrap.appendChild(title);
    headerWrap.appendChild(subtitle);
    container.appendChild(headerWrap);

    const wrap = document.createElement('div');
    wrap.className = 'tourney-results';
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    wrap.style.gap = '12px';
    wrap.style.width = '100%';
    wrap.style.maxWidth = '100%';
    wrap.style.boxSizing = 'border-box';
    const podiumWrap = this.createPodiumWrap(podium);

    if (isCompleted && awards && awards.length) {
      const { carousel, dots } = this.createAwardsCarouselElements(awards, rows, focusAwardWinnerName);
      wrap.appendChild(podiumWrap);
      wrap.appendChild(carousel);
      wrap.appendChild(dots);
      this.leaderboardHost.innerHTML = '';
      container.appendChild(wrap);
      this.leaderboardHost.appendChild(container);
      return;
    }

    let awardsEl: HTMLDivElement | null = null;
    if (awards && awards.length) {
      awardsEl = document.createElement('div');
      const title = document.createElement('div');
      title.textContent = '🏆 Awards';
      title.style.fontWeight = '700';
      title.style.margin = '16px 0 12px 0';
      title.style.fontSize = '18px';
      awardsEl.appendChild(title);

      const grid = document.createElement('div');
      grid.style.display = 'grid';
      grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
      grid.style.gap = '12px';
      grid.style.width = '100%';

      for (const a of awards) {
        const card = document.createElement('div');
        card.className = 'achievement-card';
        const emoji = document.createElement('div');
        emoji.className = 'achievement-emoji';
        emoji.textContent = this.pickAwardEmoji(a.label);
        const label = document.createElement('div');
        label.className = 'achievement-label';
        label.textContent = a.label;
        const winner = document.createElement('div');
        winner.className = 'achievement-value';
        winner.textContent = a.winner || '';
        const value = document.createElement('div');
        value.style.fontSize = '12px';
        value.style.color = '#ffa500';
        value.style.fontWeight = '500';
        value.textContent = a.value || '';
        card.appendChild(emoji);
        card.appendChild(label);
        card.appendChild(winner);
        card.appendChild(value);
        grid.appendChild(card);
      }

      awardsEl.appendChild(grid);
    }

    wrap.appendChild(podiumWrap);
    if (awardsEl) {
      wrap.appendChild(this.createDividerElement());
      wrap.appendChild(awardsEl);
    }
    wrap.appendChild(this.createDividerElement());
    wrap.appendChild(this.createPPRList(rows, 'Leaderboard', 'Points Per Roll (PPR)'));

    this.leaderboardHost.innerHTML = '';
    container.appendChild(wrap);
    this.leaderboardHost.appendChild(container);
  }

  private createDividerElement(): HTMLDivElement {
    const divider = document.createElement('div');
    divider.style.height = '1px';
    divider.style.background = 'rgba(255,255,255,0.12)';
    divider.style.margin = '4px 0 2px 0';
    return divider;
  }

  private createPodiumWrap(podium: { first?: any; second?: any; third?: any }): HTMLDivElement {
    const podiumWrap = document.createElement('div');
    podiumWrap.className = 'tourney-podium';
    const makeBlock = (labelOrObj: any, place: 'first'|'second'|'third') => {
      const label = typeof labelOrObj === 'string' ? (labelOrObj || '') : (labelOrObj?.name || '');
      const avatar = typeof labelOrObj === 'object' ? (labelOrObj?.avatar || '') : '';
      const block = document.createElement('div');
      block.className = `tourney-podium-block place-${place}`;
      const bar = document.createElement('div');
      bar.className = 'tourney-podium-bar';
      const placeEl = document.createElement('div');
      placeEl.className = 'tourney-podium-rank';
      placeEl.textContent = place === 'first' ? '🥇 1st' : place === 'second' ? '🥈 2nd' : '🥉 3rd';
      bar.appendChild(placeEl);
      block.appendChild(bar);

      const nameEl = document.createElement('div');
      nameEl.className = 'tourney-podium-name';
      {
        const img = document.createElement('img');
        img.src = avatar || avatarDataUriForName(label);
        img.alt = label;
        img.className = 'tourney-podium-avatar';
        nameEl.appendChild(img);
      }
      const nameText = document.createElement('span');
      nameText.textContent = label || '';
      nameEl.appendChild(nameText);
      block.appendChild(nameEl);
      return block;
    };

    podiumWrap.appendChild(makeBlock(podium.second || '', 'second'));
    podiumWrap.appendChild(makeBlock(podium.first || '', 'first'));
    podiumWrap.appendChild(makeBlock(podium.third || '', 'third'));
    return podiumWrap;
  }

  private createAwardsCarouselElements(
    awards: Array<{ label: string; winner?: string; value?: string }>,
    pprRowsArg: Array<{ userName: string; ppr: number; isMe?: boolean }>,
    focusAwardWinnerName?: string
  ): { carousel: HTMLDivElement; dots: HTMLDivElement } {
    const carousel = document.createElement('div');
    carousel.className = 'tourney-carousel';
    const inner = document.createElement('div');
    inner.className = 'tourney-carousel-inner';
    carousel.appendChild(inner);

    const pane1 = document.createElement('div');
    pane1.className = 'tourney-carousel-pane';
    const myAwards = awards.filter(a => a.winner === focusAwardWinnerName);
    const displayAwards = myAwards.length ? myAwards : [awards[0]];
    const mineBox = document.createElement('div');
    mineBox.className = 'tourney-my-awards';
    const headerWrap = document.createElement('div');
    headerWrap.className = 'tourney-pane-header';
    const icon = document.createElement('div'); icon.className = 'tourney-trophy'; icon.textContent = '🏆';
    const header = document.createElement('div');
    header.className = 'tourney-pane-title';
    header.textContent = (focusAwardWinnerName || displayAwards[0].winner || '') + "'s Awards";
    headerWrap.appendChild(icon); headerWrap.appendChild(header); mineBox.appendChild(headerWrap);
    const gridMine = document.createElement('div');
    gridMine.style.display = 'grid';
    gridMine.style.gridTemplateColumns = 'repeat(2, 1fr)';
    gridMine.style.gap = '12px';
    gridMine.style.width = '100%';
    displayAwards.forEach((a) => {
      const card = document.createElement('div');
      card.className = 'achievement-card';
      const emoji = document.createElement('div');
      emoji.className = 'achievement-emoji';
      emoji.textContent = this.pickAwardEmoji(a.label);
      const label = document.createElement('div');
      label.className = 'achievement-label';
      label.textContent = a.label;
      const winner = document.createElement('div');
      winner.className = 'achievement-value';
      winner.textContent = a.winner || '';
      const value = document.createElement('div');
      value.style.fontSize = '12px';
      value.style.color = '#ffa500';
      value.style.fontWeight = '500';
      value.textContent = a.value || '';
      card.appendChild(emoji);
      card.appendChild(label);
      card.appendChild(winner);
      card.appendChild(value);
      gridMine.appendChild(card);
    });
    mineBox.appendChild(gridMine);
    pane1.appendChild(mineBox);

    const pane2 = document.createElement('div');
    pane2.className = 'tourney-carousel-pane';
    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    grid.style.gap = '12px';
    grid.style.width = '100%';
    for (const a of awards) {
      const card = document.createElement('div');
      card.className = 'achievement-card';
      const emoji = document.createElement('div');
      emoji.className = 'achievement-emoji';
      emoji.textContent = this.pickAwardEmoji(a.label);
      const label = document.createElement('div');
      label.className = 'achievement-label';
      label.textContent = a.label;
      const winner = document.createElement('div');
      winner.className = 'achievement-value';
      winner.textContent = a.winner || '';
      const value = document.createElement('div');
      value.style.fontSize = '12px';
      value.style.color = '#ffa500';
      value.style.fontWeight = '500';
      value.textContent = a.value || '';
      card.appendChild(emoji);
      card.appendChild(label);
      card.appendChild(winner);
      card.appendChild(value);
      grid.appendChild(card);
    }
    pane2.appendChild(grid);

    const pane3 = document.createElement('div');
    pane3.style.flex = '0 0 100%';
    pane3.style.minWidth = '100%';
    pane3.style.boxSizing = 'border-box';
    pane3.style.padding = '12px';
    const leaderboardRows: Array<{ userName: string; ppr: number; isMe?: boolean }> = (pprRowsArg || [])
      .slice()
      .sort((a, b) => b.ppr - a.ppr);
    pane3.appendChild(this.createPPRList(leaderboardRows, 'Leaderboard', 'Points Per Roll (PPR)'));

    inner.appendChild(pane1);
    inner.appendChild(pane2);
    inner.appendChild(pane3);

    const dots = document.createElement('div');
    dots.className = 'tourney-dots';
    const mkDot = (active:boolean) => { const d = document.createElement('div'); d.className = 'tourney-dot' + (active ? ' active' : ''); return d; };
    const dotEls = [mkDot(true), mkDot(false), mkDot(false)]; dotEls.forEach(d => dots.appendChild(d));

    let index = 0; let startX = 0; let dx = 0; let touching = false;
    const updateTransform = () => { const w = carousel.clientWidth; inner.style.transform = `translateX(${-index * w}px)`; };
    const setIndex = (i:number) => { index = Math.max(0, Math.min(2, i)); updateTransform(); dotEls.forEach((d,j)=> d.style.background = j===index? '#fff' : 'rgba(255,255,255,0.4)'); };
    carousel.addEventListener('touchstart', (e)=> { if (e.touches.length!==1) return; touching = true; startX = e.touches[0].clientX; dx = 0; }, { passive: true });
    carousel.addEventListener('touchmove', (e)=> { if (!touching) return; dx = e.touches[0].clientX - startX; }, { passive: true });
    carousel.addEventListener('touchend', ()=> { if (!touching) return; touching=false; if (Math.abs(dx) > 40) setIndex(index + (dx<0?1:-1)); dx=0; });
    let dragging = false; let mouseStartX = 0; let mouseDx = 0;
    carousel.style.cursor = 'grab';
    const onMouseDown = (e: MouseEvent) => { dragging = true; mouseStartX = e.clientX; mouseDx = 0; carousel.style.cursor = 'grabbing'; e.preventDefault(); };
    const onMouseMove = (e: MouseEvent) => { if (!dragging) return; mouseDx = e.clientX - mouseStartX; };
    const onMouseUp = () => { if (!dragging) return; dragging = false; carousel.style.cursor = 'grab'; if (Math.abs(mouseDx) > 40) setIndex(index + (mouseDx<0?1:-1)); mouseDx = 0; };
    carousel.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseup', onMouseUp, { passive: true });
    window.addEventListener('resize', updateTransform);
    setIndex(0);

    return { carousel, dots };
  }

  private createPPRList(rows: Array<{ userName: string; ppr: number; isMe?: boolean }>, titleText: string = 'Leaderboard', subtitleText: string = 'Points Per Roll (PPR)'): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'tourney-standings-container';

    const headerWrap = document.createElement('div');
    headerWrap.className = 'tourney-standings-header';
    const title = document.createElement('div');
    title.className = 'tourney-standings-title';
    title.textContent = titleText;
    const subtitle = document.createElement('div');
    subtitle.className = 'tourney-standings-subtitle';
    subtitle.textContent = subtitleText;
    headerWrap.appendChild(title);
    headerWrap.appendChild(subtitle);
    container.appendChild(headerWrap);

    const table = document.createElement('table');
    table.className = 'tourney-standings-table';
    const rowsCopy = rows.slice().sort((a,b)=> b.ppr - a.ppr);
    table.innerHTML = `
      <thead>
        <tr>
          <th>Player</th>
          <th>PPR</th>
        </tr>
      </thead>
      <tbody>
        ${rowsCopy.map(r => `
          <tr class="tourney-standings-row ${r.isMe ? 'current-user' : ''}">
            <td class="name-col">${r.isMe ? `<strong>* ${r.userName}</strong>` : r.userName}</td>
            <td class="record-col">${r.ppr.toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    `;
    container.appendChild(table);
    return container;
  }

  private pickAwardEmoji(label: string): string {
    const normalized = label.toLowerCase();
    if (normalized.includes('winner') || normalized.includes('champion')) return '🏆';
    if (normalized.includes('skrollr')) return '🌀';
    if (normalized.includes('fast')) return '⚡';
    if (normalized.includes('slow')) return '🐢';
    if (normalized.includes('pop')) return '🎈';
    if (normalized.includes('orbit')) return '🛰️';
    if (normalized.includes('bust')) return '💥';
    if (normalized.includes('streak')) return '🔥';
    if (normalized.includes('efficiency') || normalized.includes('ppr')) return '📐';
    if (normalized.includes('turn') || normalized.includes('score')) return '🎯';
    return '💠';
  }

  private buildStandingsAwardsPanel(opts: {
    awards: Array<{ label: string; winner?: string; value?: string }>;
    focusAwardWinnerName?: string;
    podium?: { first?: any; second?: any; third?: any };
    pprRows?: Array<{ userName: string; ppr: number; isMe?: boolean }>;
  }): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'tourney-results';
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    wrap.style.gap = '12px';
    wrap.style.width = '100%';
    wrap.style.boxSizing = 'border-box';
    wrap.style.marginTop = '12px';
    const podiumWrap = this.createPodiumWrap(opts.podium || {});
    wrap.appendChild(podiumWrap);
    const { carousel, dots } = this.createAwardsCarouselElements(opts.awards, opts.pprRows || [], opts.focusAwardWinnerName);
    wrap.appendChild(carousel);
    wrap.appendChild(dots);
    return wrap;
  }

  public showRoundRobinResultsCarousel(
    podium: { first?: any; second?: any; third?: any },
    standingsEntries: StandingsViewEntry[],
    totalMatches: number,
    currentUserId?: string,
    awards?: Array<{ label: string; winner?: string; value?: string }>,
    focusAwardWinnerName?: string
  ) {
    if (!this.leaderboardHost) return;
    this.ensureScheduleHidden();
    if (this.listEl) this.listEl.style.display = 'none';
    if (this.bracketHost) this.bracketHost.style.display = 'none';
    if (this.scheduleHost) this.scheduleHost.style.display = 'none';
    this.leaderboardHost.style.display = 'block';
    if (this.startBtn) this.startBtn.style.display = 'none';
    if (this.cancelBtn) this.cancelBtn.style.display = 'none';
    if (this.leaveBtn) this.leaveBtn.style.display = 'none';
    if (this.refreshBtn) this.refreshBtn.style.display = 'none';
    if (this.codeEl) this.codeEl.style.display = 'none';
    this.ensureStandingsHidden();
    this.leftToggle?.classList.remove('selected');
    this.rightToggle?.classList.add('selected');

    const wrap = document.createElement('div');
    wrap.className = 'tourney-results';
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    wrap.style.gap = '12px';
    wrap.style.width = '100%';
    wrap.style.maxWidth = '100%';
    wrap.style.boxSizing = 'border-box';

    const podiumWrap = this.createPodiumWrap(podium);
    wrap.appendChild(podiumWrap);

    if (awards && awards.length) {
      const { carousel, dots } = this.createRoundRobinResultsCarousel(
        awards,
        standingsEntries,
        totalMatches,
        currentUserId,
        focusAwardWinnerName
      );
      wrap.appendChild(carousel);
      wrap.appendChild(dots);
    }

    this.leaderboardHost.innerHTML = '';
    this.leaderboardHost.appendChild(wrap);
  }

  private createRoundRobinResultsCarousel(
    awards: Array<{ label: string; winner?: string; value?: string }>,
    standingsEntries: StandingsViewEntry[],
    totalMatches: number,
    currentUserId?: string,
    focusAwardWinnerName?: string
  ): { carousel: HTMLDivElement; dots: HTMLDivElement } {
    const carousel = document.createElement('div');
    carousel.className = 'tourney-carousel';
    const inner = document.createElement('div');
    inner.className = 'tourney-carousel-inner';
    carousel.appendChild(inner);

    // Pane 1: Current player's awards
    const pane1 = document.createElement('div');
    pane1.className = 'tourney-carousel-pane';
    const myAwards = awards.filter(a => a.winner === focusAwardWinnerName);
    const displayAwards = myAwards.length ? myAwards : [awards[0]];
    const mineBox = document.createElement('div');
    mineBox.className = 'tourney-my-awards';
    const headerWrap = document.createElement('div');
    headerWrap.className = 'tourney-pane-header';
    const icon = document.createElement('div');
    icon.className = 'tourney-trophy';
    icon.textContent = '🏆';
    const header = document.createElement('div');
    header.className = 'tourney-pane-title';
    header.textContent = (focusAwardWinnerName || displayAwards[0].winner || '') + "'s Awards";
    headerWrap.appendChild(icon);
    headerWrap.appendChild(header);
    mineBox.appendChild(headerWrap);
    const gridMine = document.createElement('div');
    gridMine.style.display = 'grid';
    gridMine.style.gridTemplateColumns = 'repeat(2, 1fr)';
    gridMine.style.gap = '12px';
    gridMine.style.width = '100%';
    displayAwards.forEach((a) => {
      const card = document.createElement('div');
      card.className = 'achievement-card';
      const emoji = document.createElement('div');
      emoji.className = 'achievement-emoji';
      emoji.textContent = this.pickAwardEmoji(a.label);
      const label = document.createElement('div');
      label.className = 'achievement-label';
      label.textContent = a.label;
      const winner = document.createElement('div');
      winner.className = 'achievement-value';
      winner.textContent = a.winner || '';
      const value = document.createElement('div');
      value.style.fontSize = '12px';
      value.style.color = '#ffa500';
      value.style.fontWeight = '500';
      value.textContent = a.value || '';
      card.appendChild(emoji);
      card.appendChild(label);
      card.appendChild(winner);
      card.appendChild(value);
      gridMine.appendChild(card);
    });
    mineBox.appendChild(gridMine);
    pane1.appendChild(mineBox);

    // Pane 2: All awards
    const pane2 = document.createElement('div');
    pane2.className = 'tourney-carousel-pane';
    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    grid.style.gap = '12px';
    grid.style.width = '100%';
    for (const a of awards) {
      const card = document.createElement('div');
      card.className = 'achievement-card';
      const emoji = document.createElement('div');
      emoji.className = 'achievement-emoji';
      emoji.textContent = this.pickAwardEmoji(a.label);
      const label = document.createElement('div');
      label.className = 'achievement-label';
      label.textContent = a.label;
      const winner = document.createElement('div');
      winner.className = 'achievement-value';
      winner.textContent = a.winner || '';
      const value = document.createElement('div');
      value.style.fontSize = '12px';
      value.style.color = '#ffa500';
      value.style.fontWeight = '500';
      value.textContent = a.value || '';
      card.appendChild(emoji);
      card.appendChild(label);
      card.appendChild(winner);
      card.appendChild(value);
      grid.appendChild(card);
    }
    pane2.appendChild(grid);

    const pprRows = standingsEntries.map(entry => ({
      userName: entry.userName,
      ppr: (entry as any).ppr ?? 0,
      isMe: entry.userId === currentUserId
    }));
    // Pane 3: PPR leaderboard (standings style)
    const pane3 = document.createElement('div');
    pane3.className = 'tourney-carousel-pane tourney-carousel-pane-standings';
    pane3.style.flex = '0 0 100%';
    pane3.style.minWidth = '100%';
    pane3.style.boxSizing = 'border-box';
    pane3.style.padding = '12px';
    const leaderboardRows: Array<{ userName: string; ppr: number; isMe?: boolean }> = pprRows
      .slice()
      .sort((a, b) => b.ppr - a.ppr);
    pane3.appendChild(this.createPPRList(leaderboardRows, 'Leaderboard', 'Points Per Roll (PPR)'));

    inner.appendChild(pane1);
    inner.appendChild(pane2);
    inner.appendChild(pane3);

    const dots = document.createElement('div');
    dots.className = 'tourney-dots';
    const mkDot = (active: boolean) => {
      const d = document.createElement('div');
      d.className = 'tourney-dot' + (active ? ' active' : '');
      return d;
    };
    const dotEls = [mkDot(true), mkDot(false), mkDot(false)];
    dotEls.forEach(d => dots.appendChild(d));

    let index = 0;
    let startX = 0;
    let dx = 0;
    let touching = false;
    const updateTransform = () => {
      const w = carousel.clientWidth;
      inner.style.transform = `translateX(${-index * w}px)`;
    };
    const setIndex = (i: number) => {
      index = Math.max(0, Math.min(2, i));
      updateTransform();
      dotEls.forEach((d, j) => (d.style.background = j === index ? '#fff' : 'rgba(255,255,255,0.4)'));
    };
    carousel.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        touching = true;
        startX = e.touches[0].clientX;
        dx = 0;
      },
      { passive: true }
    );
    carousel.addEventListener(
      'touchmove',
      (e) => {
        if (!touching) return;
        dx = e.touches[0].clientX - startX;
      },
      { passive: true }
    );
    carousel.addEventListener('touchend', () => {
      if (!touching) return;
      touching = false;
      if (Math.abs(dx) > 40) setIndex(index + (dx < 0 ? 1 : -1));
      dx = 0;
    });
    let dragging = false;
    let mouseStartX = 0;
    let mouseDx = 0;
    carousel.style.cursor = 'grab';
    const onMouseDown = (e: MouseEvent) => {
      dragging = true;
      mouseStartX = e.clientX;
      mouseDx = 0;
      carousel.style.cursor = 'grabbing';
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      mouseDx = e.clientX - mouseStartX;
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      carousel.style.cursor = 'grab';
      if (Math.abs(mouseDx) > 40) setIndex(index + (mouseDx < 0 ? 1 : -1));
      mouseDx = 0;
    };
    carousel.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseup', onMouseUp, { passive: true });
    window.addEventListener('resize', updateTransform);
    setIndex(0);

    return { carousel, dots };
  }

  private playerRow(p: TournamentPlayerVM): string {
    const seed = p.seed ?? '';
    const avatarUrl = p.profileImageUrl || avatarDataUriForName(p.userName);
    const img = `<img class="tourney-avatar" src="${avatarUrl}" alt="${p.userName}" />`;
    const host = p.isHost ? `<span class="tourney-host-badge" title="Host">*</span>` : '';
    return `
      <div class="tourney-player-row">
        <div class="tourney-seed">${host}${seed}</div>
        ${img}
        <div class="tourney-name">${p.userName}</div>
      </div>
    `;
  }

  private template(tournamentName: string, isCreator: boolean, code?: string, tournamentType?: 'bracket' | 'round_robin') {
    const actions = isCreator
      ? `
          <button id="tourneyCancelBtn" class="skeech-btn-icon"><div class="icon">❌</div><div class="text">Cancel Tournament</div></button>
          <button id="tourneyBackBtn" class="skeech-btn-icon"><div class="icon">↩️</div><div class="text">Back to Lobby</div></button>
          <button id="tourneyStartBtn" class="skeech-btn-icon"><div class="icon">🏆</div><div class="text">Start Tournament</div></button>
        `
      : `
          <button id="tourneyLeaveBtn" class="skeech-btn-icon"><div class="icon">👋</div><div class="text">Leave Tournament</div></button>
          <button id="tourneyBackBtn" class="skeech-btn-icon"><div class="icon">↩️</div><div class="text">Back to Lobby</div></button>
        `;
    const typeLabel = tournamentType === 'round_robin' ? 'Round Robin' : 'Bracket';
    return `
      <div class="tourney-lobby-wrap">
        <div class="tourney-lobby-header">
          <div style="display:flex;flex-direction:column;gap:4px;">
            <div class="tourney-lobby-title">${tournamentName}</div>
            <div class="tourney-lobby-subtitle" id="tourneyTypeLabel">${typeLabel}</div>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="flex: 1;">
              <div id="tourneyPlayerCount" class="tourney-player-count"></div>
              ${code ? `<div id="tourneyCode" class="tourney-code">Code: <strong>${code}</strong></div>` : '<div id="tourneyCode" class="tourney-code"></div>'}
            </div>
            <button id="tourneyRefreshBtn" class="skeech-btn-icon" style="padding: 4px 8px; font-size: 18px;" title="Refresh player list">🔄️</button>
          </div>
        </div>
        <div id="tourneyLobbyList" class="tourney-lobby-list"></div>
        <div id="tourneyBracket" class="tourney-bracket-viewport" style="display:none"></div>
        <div id="tourneySchedule" class="tourney-schedule-host" style="display:none"></div>
        <div id="tourneyLeaderboard" class="tourney-leaderboard" style="display:none"></div>
        <div class="tourney-lobby-actions">${actions}</div>
      </div>
    `;
  }
}







